import { Kbd, Text, UnstyledButton } from '@mantine/core';
import { Spotlight, spotlight, type SpotlightActionGroupData } from '@mantine/spotlight';
import { IconBuildingStore, IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, type DealerRow } from '../lib/api.js';
import { NAV } from '../routes/adminNav.js';
import classes from './CommandSearch.module.css';

/**
 * Global search: jump to any page or dealership from anywhere (Ctrl/Cmd+K).
 *
 * Pages are filtered by the caller's permissions, exactly like the sidebar. The
 * dealer list is only requested when the user may read dealers, and it reuses
 * the `['dealers']` query the Overview already keeps warm.
 */
export function CommandSearch({ permissions }: { permissions: string[] }) {
  const navigate = useNavigate();
  const canSeeDealers = permissions.includes('dealers:read');

  const dealersQ = useQuery({
    queryKey: ['dealers'],
    queryFn: () => api.get<{ dealers: DealerRow[] }>('/admin/dealers'),
    enabled: canSeeDealers,
    staleTime: 60_000,
  });

  const pages = NAV.filter((n) => !n.permission || permissions.includes(n.permission)).map((n) => ({
    id: n.to,
    label: n.label,
    description: n.hint,
    leftSection: <n.icon size={18} stroke={1.7} />,
    onClick: () => navigate(n.to),
  }));

  const dealers = (dealersQ.data?.dealers ?? []).map((d) => ({
    id: `dealer-${d.id}`,
    label: d.orgName || d.id,
    description: `Dealership · ${d.id}`,
    keywords: [d.id],
    leftSection: <IconBuildingStore size={18} stroke={1.7} />,
    onClick: () => navigate(`/app/dealers/${d.id}`),
  }));

  const groups: SpotlightActionGroupData[] = [
    { group: 'Pages', actions: pages },
    ...(dealers.length ? [{ group: 'Dealerships', actions: dealers }] : []),
  ];

  return (
    <>
      <UnstyledButton className={classes.trigger} onClick={() => spotlight.open()} aria-label="Search">
        <IconSearch size={15} stroke={1.8} />
        <Text size="sm" c="dimmed" className={classes.triggerText}>
          Search pages and dealerships
        </Text>
        <Kbd size="xs" className={classes.kbd}>
          Ctrl K
        </Kbd>
      </UnstyledButton>

      <Spotlight
        actions={groups}
        shortcut={['mod + K', '/']}
        highlightQuery
        scrollable
        maxHeight={420}
        nothingFound="Nothing matches that."
        searchProps={{
          leftSection: <IconSearch size={18} stroke={1.7} />,
          placeholder: 'Search pages and dealerships…',
        }}
      />
    </>
  );
}
